import { Tracker } from '@relewise/client';
import { RelewiseCart, RelewiseOrder } from './RelewiseTypes';
import { relewiseUserService } from './UserService';

interface InitializedService {
    tracker: Tracker;
    disabled: false;
}

class RelewiseTrackingService {
    tracker: Tracker | undefined;
    protected disabled = true;
    private isDev = process.env.NODE_ENV === 'development';

    constructor() {
        // Relewise tracking will only run in client side
        if (typeof window === 'undefined') {
            return;
        }

        const { relewiseApiKey, relewiseDatasetId, relewiseUrl } = window.___initialData___?.requestContext.app.config ?? {};
        if (!relewiseApiKey || !relewiseDatasetId || !relewiseUrl) {
            console.warn(
                '[Relewise Tracking Service] Missing Relewise configuration. Expected relewiseApiKey, relewiseDatasetId and relewiseUrl. Add this config in the CMS.'
            );
            return;
        }

        this.tracker = new Tracker(relewiseDatasetId, relewiseApiKey, {
            serverUrl: relewiseUrl
        });

        this.disabled = false;
    }

    private logEvent(name: string, payload: Record<string, unknown>) {
        if (this.isDev) {
            console.log(`[Relewise Tracking Service] ${name}:`, payload);
        }
    }

    private isEnabled(): this is InitializedService {
        return !this.disabled && !!this.tracker;
    }

    public async trackProductView(productId: string, variantId?: string) {
        if (!this.isEnabled()) {
            return;
        }

        try {
            const user = await relewiseUserService.getUser();
            const payload = {
                productId,
                user,
                ...(variantId ? { variantId } : {})
            };

            this.logEvent('Product View', payload);
            await this.tracker.trackProductView(payload);
        } catch (err) {
            console.error('[Relewise Tracking Service] tracking failed', err);
        }
    }

    public async trackProductEngagement(productId: string, isFavorite: boolean, variantId?: string) {
        if (!this.isEnabled()) {
            return;
        }

        try {
            const user = await relewiseUserService.getUser();
            const payload = {
                user,
                product: {
                    productId,
                    ...(variantId ? { variantId } : {})
                },
                engagement: {
                    isFavorite
                }
            };

            this.logEvent('Product Engagement', payload);
            await this.tracker.trackProductEngagement(payload);
        } catch (err) {
            console.error('[Relewise Tracking Service] tracking failed', err);
        }
    }

    public async trackCategoryView(listId: string) {
        if (!this.isEnabled()) {
            return;
        }

        try {
            const user = await relewiseUserService.getUser();
            const payload = {
                idPath: [listId],
                user
            };

            this.logEvent('Product List View', payload);
            await this.tracker.trackProductCategoryView(payload);
        } catch (err) {
            console.error('[Relewise Tracking Service] tracking failed', err);
        }
    }

    public async trackOrder(order: RelewiseOrder) {
        if (!this.isEnabled()) {
            return;
        }

        try {
            const user = await relewiseUserService.getUser();
            const { lineItems, subtotal, orderNumber } = order;
            const payload = {
                lineItems,
                subtotal,
                orderNumber,
                user,
                cartName: 'default'
            };

            this.logEvent('Order', payload);
            await this.tracker.trackOrder(payload);
        } catch (err) {
            console.error('[Relewise Tracking Service] tracking failed', err);
        }
    }

    public async trackCart(cart: RelewiseCart) {
        if (!this.isEnabled()) {
            return;
        }

        try {
            const user = await relewiseUserService.getUser();
            const { lineItems, subtotal } = cart;
            const payload = {
                lineItems,
                subtotal,
                user,
                cartName: 'default'
            };

            this.logEvent('Cart', payload);
            await this.tracker.trackCart(payload);
        } catch (err) {
            console.error('[Relewise Tracking Service] tracking failed', err);
        }
    }

    public async trackUserUpdate() {
        if (!this.isEnabled()) {
            return;
        }

        try {
            const user = await relewiseUserService.getUser();
            const payload = {
                user
            };

            this.logEvent('User update', payload);
            await this.tracker.trackUserUpdate(payload);
        } catch (err) {
            console.error('[Relewise Tracking Service] tracking failed', err);
        }
    }
}

export const relewiseTrackingService = new RelewiseTrackingService();
